/**
 * Custom hooks for Incoterms API
 *
 * Provides hooks for listing Incoterms 2020 with descriptions for tooltips
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// ============================================================================
// TYPES
// ============================================================================

export interface IncotermItem {
  code: string;
  name: string;
  version: string;
  category: string;
  category_name: string;
  risk_transfer_point: string;
  description: string | null;
  seller_pays_freight: boolean;
  seller_pays_insurance: boolean;
  suitable_for_sea_only: boolean;
  seller_customs_export: boolean;
  seller_customs_import: boolean;
  seller_unloads: boolean;
}

export interface IncotermListResponse {
  incoterms: IncotermItem[];
  total: number;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const INCOTERMS_QUERY_KEYS = {
  all: ["incoterms"] as const,
  list: (category?: string, seaOnly?: boolean) =>
    [...INCOTERMS_QUERY_KEYS.all, "list", category, seaOnly] as const,
  byCode: (code: string) => [...INCOTERMS_QUERY_KEYS.all, code] as const,
};

// ============================================================================
// LIST INCOTERMS HOOK
// ============================================================================

/**
 * Hook to fetch all Incoterms with descriptions
 *
 * @param category - Optional category filter (E, F, C, D)
 * @param seaOnly - Optional filter for sea/waterway only terms
 * @param enabled - Whether the query should be enabled
 *
 * Returns all 11 Incoterms 2020 with:
 * - Full name and description for tooltips
 * - Category information
 * - Responsibility flags (freight, insurance, customs)
 */
export function useIncoterms(
  category?: string,
  seaOnly?: boolean,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: INCOTERMS_QUERY_KEYS.list(category, seaOnly),
    queryFn: async (): Promise<IncotermListResponse> => {
      const params: Record<string, string | boolean> = {};
      if (category) params.category = category;
      if (seaOnly !== undefined) params.sea_only = seaOnly;

      return await apiGet<IncotermListResponse>("/incoterms", { params });
    },
    enabled,
    staleTime: Infinity, // Incoterms are static reference data
  });
}

// ============================================================================
// GET INCOTERM BY CODE HOOK
// ============================================================================

/**
 * Hook to fetch a single Incoterm by code
 *
 * @param code - 3-letter Incoterm code (e.g., FOB, CIF, DDP)
 * @param enabled - Whether the query should be enabled
 */
export function useIncotermByCode(code: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: INCOTERMS_QUERY_KEYS.byCode(code || ""),
    queryFn: async (): Promise<IncotermItem> => {
      if (!code) throw new Error("No Incoterm code provided");
      return await apiGet<IncotermItem>(`/incoterms/${code}`);
    },
    enabled: enabled && !!code,
    staleTime: Infinity, // Incoterms are static
  });
}
